import React, {useMemo, useRef, useState} from 'react';
import {Col, FormGroup, Input, Label, Row} from 'reactstrap';
import {Form} from '@unform/web';
import './device.sass';
import {FieldSet} from '../../components/fieldset';
import I18nMessage from '../../components/I18nMessage';
import {Input as UInput} from '../../components/input';
import {GroupButtonForm} from '../../components/group-button-form';
import ReactSelect from '../../components/react-select';
import ReactSelectCategory from '../../components/react-select-category';
import ToggleButton from '../../components/toggle-button';
import {kmhToKnots, knotsToKmH} from '../../helpers/conversions';
import {PlatformIntl} from '../../infra/PlatformIntl';
import Yup from '../../infra/Yup';
import Button from '../../components/button';
import {useDropzone, ErrorCode} from 'react-dropzone';
import {useIntl} from 'react-intl';
import useAlerts from 'src/hooks/use-alerts';
import Tooltip from 'src/autobahn/components/tooltip';
import useCreateDeviceVideModel from './useCreateDeviceViewModel.js';
import {ACCEPTED_IMAGE_EXTENSIONS, MAX_DEVICE_IMAGES, MAX_IMAGE_SIZE} from './constants.js';
import DeviceImagesOverlay from './device-images-overlay.jsx';
import Dialog from 'src/autobahn/components/dialog';
import IconButton from 'src/autobahn/components/button/icon-button';
import {AsyncTypeahead} from 'react-bootstrap-typeahead';
import DeviceFipeService from '../../services/device-fipe-service';
import {useIntegrations} from '../../autobahn/features/integration/queries';
import IntegrationAccessToggles from '../../autobahn/features/integration/access-toggles';

const platformIntl = PlatformIntl.getInstance();
const fipeService = new DeviceFipeService();

const schema = Yup.object().shape({
    id: Yup.number().notRequired(),
    name: Yup.string().label(platformIntl.formatMessage('sharedName')).required(),
    uniqueId: Yup.string().label(platformIntl.formatMessage('deviceImei')).required(),
    groupId: Yup.string().notRequired(),
    customerId: Yup.number(), // .required(), // TODO: make it required again when asked
    phone: Yup.string().notRequired(),
    model: Yup.string().notRequired(),
    contact: Yup.string().notRequired(),
    category: Yup.string().notRequired(),
    disabled: Yup.boolean().notRequired(),
    shareable: Yup.boolean().notRequired(),
    mirrorable: Yup.boolean().notRequired(),
    attributes: Yup.object().shape({
        cc_email: Yup.string().notRequired(),
        m2mProvider: Yup.string().notRequired(),
        iccid: Yup.string().notRequired(),
        vehicleModel: Yup.string().notRequired(),
        plate: Yup.string().notRequired(),
        year: Yup.string().notRequired(),
        fuelType: Yup.string().notRequired(),
        fuelConsumption: Yup.string().notRequired(),
        manufacturer: Yup.string().notRequired(),
        color: Yup.string().notRequired(),
        renavam: Yup.string().notRequired(),
        chassi: Yup.string().notRequired(),
        devicePassword: Yup.string().notRequired(),
        speedLimit: Yup.number().label(platformIntl.formatMessage('attributeSpeedLimit'))
            .when(([value], schema) => {
                if (knotsToKmH(value) < 0) {
                    return schema.positive();
                }
                return schema;
            }),
        installationLocation: Yup.string().notRequired(),
        technician: Yup.string().notRequired(),
        ignoreOdometer: Yup.boolean().default(false),
    }),
});

// O servidor le a chave report.ignoreOdometer. O @unform trata o ponto do nome do campo como objeto aninhado, entao o
// campo do formulario se chama ignoreOdometer e a chave real e traduzida na ida e na volta. Um ignoreOdometer solto,
// gravado antes disso, nunca teve efeito no servidor: a tela mostra desligado e o descarta ao salvar.
const IGNORE_ODOMETER_KEY = 'report.ignoreOdometer';

const isOn = (value) => value === true || value === 'true';

const cast = (src, speedLimitTransformer, toForm = false) => {
    const newData = {...src};

    if (newData.attributes) {
        const newAttributes = {...src.attributes};
        if (newAttributes.hasOwnProperty('speedLimit')) {
            newAttributes.speedLimit = speedLimitTransformer(src.attributes.speedLimit);
        }

        if (toForm) {
            newAttributes.ignoreOdometer = isOn(newAttributes[IGNORE_ODOMETER_KEY]);
            delete newAttributes[IGNORE_ODOMETER_KEY];
        } else {
            // Desligado nao grava a chave: o dispositivo herda do grupo, como antes de o toggle existir.
            if (isOn(newAttributes.ignoreOdometer)) {
                newAttributes[IGNORE_ODOMETER_KEY] = true;
            } else {
                delete newAttributes[IGNORE_ODOMETER_KEY];
            }
            delete newAttributes.ignoreOdometer;
        }

        newData.attributes = newAttributes;
    }

    if (newData.category === null) {
        newData.category = undefined;
    } else if (typeof newData.category?.id === 'string') {
        newData.category = newData.category.id;
    }

    return newData;
};

const DeviceAdminForm = (props) => {
    const {
        cancel,
        data,
        edit,
        newRegister,
        updateRegister,
        groups,
        equipments,
        categories,
        loading,
        customers,
        deviceStore,
        session,
    } = props;

    const {toast} = useAlerts();
    const intl = useIntl();

    // Pela central que governa a cadeia, nao pelo customer do dispositivo: cliente gerenciado nao
    // tem liberacao propria (mesmo ajuste ja feito em user-form.jsx pro MON-555).
    const {data: centralIntegrations} = useIntegrations(
        data?.ownerCustomerId ? {all: true, customerId: data.ownerCustomerId} : {all: true});
    const availableIntegrations = useMemo(() => centralIntegrations.filter(
        (integration) => ['PRF', 'Herrlog'].includes(integration.title),
    ), [centralIntegrations]);

    const prfIntegration = availableIntegrations.find((integration) => integration.title === 'PRF');
    const herrlogIntegration = availableIntegrations.find((integration) => integration.title === 'Herrlog');

    const [integrationFlags, setIntegrationFlags] = useState({
        shareable: data?.shareable ?? false,
        mirrorable: data?.mirrorable ?? false,
    });

    const selectedIntegrationIds = [
        prfIntegration && integrationFlags.shareable ? prfIntegration.id : null,
        herrlogIntegration && integrationFlags.mirrorable ? herrlogIntegration.id : null,
    ].filter(Boolean);

    const onIntegrationToggle = (nextIds) => setIntegrationFlags((flags) => ({
        shareable: prfIntegration ? nextIds.includes(prfIntegration.id) : flags.shareable,
        mirrorable: herrlogIntegration ? nextIds.includes(herrlogIntegration.id) : flags.mirrorable,
    }));

    const isSuperiorAccount = !!session && (session.administrator || session.userLimit !== 0);
    const showIntegrations = edit && !!data?.id && isSuperiorAccount && availableIntegrations.length > 0;

    const device = useMemo(() => {
        if (edit && data?.id) return deviceStore.findById(data.id);
        return null;
    }, [edit, data?.id]);

    const {images, imagesAmount, imageEditionLocked, loadingDeviceImages, addImage, removeImage, pinImage, unlockImageEdition} = useCreateDeviceVideModel(device);

    const [isUnlockDialogOpen, setUnlockDialogOpen] = useState(false);
    const [ignoreOdometer, setIgnoreOdometer] = useState(isOn(data?.attributes?.[IGNORE_ODOMETER_KEY]));

    const openUnlockDialog = () => {
        setUnlockDialogOpen(true);
    };

    const closeUnlockDialog = () => {
        setUnlockDialogOpen(false);
    };

    const acceptUnlockDialog = () => {
        closeUnlockDialog();
        unlockImageEdition();
    };

    const imagesOverlayRef = useRef(null);

    const isDropzoneInteractionDisabled = imagesAmount > 0;

    const onPressDeviceImage = (file) => () => {
        imagesOverlayRef.current.open(file);
    };

    const onDropDeviceImages = (acceptedFiles, rejectedFiles) => {
        if (rejectedFiles.length > 0) {
            const [firstRejectedFile] = rejectedFiles;
            const [firstError] = firstRejectedFile.errors;

            let errorMessage;
            switch (firstError.code) {
                case ErrorCode.FileInvalidType:
                    errorMessage = intl.formatMessage({id: ErrorCode.FileInvalidType}, {
                        allowed_ext: '".jpeg, .png"',
                    });
                    break;

                case ErrorCode.FileTooLarge:
                    errorMessage = intl.formatMessage({id: ErrorCode.FileTooLarge}, {
                        max_allowed_size: `${MAX_IMAGE_SIZE}MB`,
                    });
                    break;

                case ErrorCode.FileTooSmall:
                    errorMessage = intl.formatMessage({id: ErrorCode.FileTooSmall}, {
                        min_allowed_size: '0b',
                    });
                    break;

                case ErrorCode.TooManyFiles:
                    errorMessage = intl.formatMessage({id: ErrorCode.TooManyFiles}, {
                        max_allowed_files: MAX_DEVICE_IMAGES,
                    });
                    break;

                default:
                    errorMessage = intl.formatMessage({id: 'error_unknown'});
            }

            toast.error(errorMessage);
        }

        addImage(acceptedFiles);
    };

    const {
        getRootProps,
        getInputProps,
        open: openDeviceImagesPicker,
        rootRef,
    } = useDropzone({
        disabled: loadingDeviceImages,
        noClick: isDropzoneInteractionDisabled,
        noDrag: isDropzoneInteractionDisabled,
        noKeyboard: isDropzoneInteractionDisabled,
        noDragEventsBubbling: isDropzoneInteractionDisabled,
        onDragEnter: () => {
            rootRef.current.setAttribute('style', 'background-color: #16A5DA26');
        },
        onDragLeave: () => {
            rootRef.current.setAttribute('style', undefined);
        },
        onDropAccepted: () => {
            rootRef.current.setAttribute('style', undefined);
        },
        onDropRejected: () => {
            rootRef.current.setAttribute('style', undefined);
        },
        accept: Object.values(ACCEPTED_IMAGE_EXTENSIONS).reduce((accu, ext) => {
            accu[ext] = [];
            return accu;
        }, {}),
        maxFiles: MAX_DEVICE_IMAGES,
        maxSize: MAX_IMAGE_SIZE,
        onDrop: onDropDeviceImages,
    });

    const formRef = useRef(null);

    const handleF8Key = () => {
        formRef.current.submitForm();
    };

    const [fipeOptions, setFipeOptions] = useState([]);
    const [fipeLoading, setFipeLoading] = useState(false);
    // Guarda contra respostas fora de ordem (promise antiga resolvendo depois da mais
    // recente) sobrescrevendo o estado com dado velho - só a última busca disparada pode aplicar.
    const fipeRequestId = useRef(0);

    const handleFipeInputChange = (text) => {
        formRef.current.setFieldValue('attributes.vehicleModel', text);
    };

    const handleFipeSearch = (query) => {
        const requestId = ++fipeRequestId.current;
        setFipeLoading(true);
        fipeService.search(query)
            .then((results) => {
                if (requestId === fipeRequestId.current) {
                    setFipeOptions(results || []);
                }
            })
            .catch(() => {
                if (requestId === fipeRequestId.current) {
                    toast.error(intl.formatMessage({id: 'deviceFipeSearchError'}));
                }
            })
            .finally(() => {
                if (requestId === fipeRequestId.current) {
                    setFipeLoading(false);
                }
            });
    };

    const handleFipeSelect = (selected) => {
        const item = selected && selected[0];
        if (!item) return;
        const requestId = ++fipeRequestId.current;
        setFipeLoading(true);
        fipeService.resolve(item)
            .then((vehicle) => {
                if (requestId !== fipeRequestId.current) return;
                formRef.current.setFieldValue('attributes.manufacturer', vehicle.manufacturer);
                formRef.current.setFieldValue('attributes.vehicleModel', vehicle.vehicleModel);
                formRef.current.setFieldValue('attributes.year', vehicle.year);
                formRef.current.setFieldValue('attributes.fuelType', vehicle.fuelType);
            })
            .catch(() => {
                if (requestId === fipeRequestId.current) {
                    toast.error(intl.formatMessage({id: 'deviceFipeResolveError'}));
                }
            })
            .finally(() => {
                if (requestId === fipeRequestId.current) {
                    setFipeLoading(false);
                }
            });
    };

    const handleSubmit = async (payload) => {
        try {
            const payloadData = cast(payload, kmhToKnots);

            await schema.validate(payloadData, {
                abortEarly: false,
            });

            if (edit) {
                return updateRegister(payloadData, imageEditionLocked ? null : images);
            }

            return newRegister(payloadData, images);
        } catch (err) {
            const validationErrors = {};
            if (err instanceof Yup.ValidationError) {
                err.inner.forEach((error) => {
                    validationErrors[error.path] = error.message;
                });
                formRef.current.setErrors(validationErrors);
            }
        }
    };

    return (
        <>
            <Form
                ref={formRef}
                onSubmit={handleSubmit}
                initialData={cast(data, knotsToKmH, true)}>

                {!!edit && <UInput hidden type="number" name="id" />}

                <FieldSet className="text-left border p-4" title={<I18nMessage id="sharedIdentification" />}>
                    <>
                        <Row>
                            <Col>
                                <FormGroup className="custom-formGroup">
                                    <Label for="name"><I18nMessage id="sharedName" />*</Label>
                                    <Input
                                        tag={UInput}
                                        type="text"
                                        name="name"
                                        id="name"
                                    />
                                </FormGroup>
                            </Col>
                            <Col>
                                <FormGroup className="custom-formGroup">
                                    <Label for="uniqueId"><I18nMessage id="deviceImei" />*</Label>
                                    <Input
                                        tag={UInput}
                                        type="text"
                                        name="uniqueId"
                                        id="uniqueId"
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <FormGroup className="custom-formGroup">
                                    <Label for="category"><I18nMessage id="deviceCategory" /></Label>
                                    <ReactSelectCategory
                                        id={'category'}
                                        name="category"
                                        options={categories} />
                                </FormGroup>
                            </Col>
                            <Col>
                                <FormGroup className="custom-formGroup">
                                    <Label for="groupId"><I18nMessage id="groupParent" /></Label>
                                    <ReactSelect
                                        id={'groupId'}
                                        name="groupId"
                                        options={groups} />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <FormGroup className="custom-formGroup">
                                    <Label for="customerId"><I18nMessage id="sharedCustomer" /></Label>
                                    <ReactSelect
                                        id={'customerId'}
                                        name="customerId"
                                        options={customers} />
                                </FormGroup>
                            </Col>
                            <Col>
                                <FormGroup className="custom-formGroup">
                                    <Label for="contact"><I18nMessage id="deviceContact" /></Label>
                                    <Input
                                        tag={UInput}
                                        type="text"
                                        name="contact"
                                        id="contact"
                                    />
                                </FormGroup>
                            </Col>
                            <Col>
                                <FormGroup className="custom-formGroup">
                                    <Label for="disabled"><I18nMessage id="sharedDisabled" /></Label>
                                    <ToggleButton
                                        name={'disabled'}
                                        id={'disabled'} />
                                </FormGroup>
                            </Col>
                        </Row>
                    </>
                </FieldSet>
                <br />
                <FieldSet className="text-left border p-4" title={<I18nMessage id="sharedTracker" />}>
                    <>
                        <Row>
                            <Col>
                                <FormGroup className="custom-formGroup">
                                    <Label for="model"><I18nMessage id="deviceModel" /></Label>
                                    <ReactSelect id={'model'} name="model" options={equipments} />
                                </FormGroup>
                                <FormGroup className="custom-formGroup">
                                    <Label for="iccid"><I18nMessage id="deviceIccid" /></Label>
                                    <Input
                                        tag={UInput}
                                        type="text"
                                        name="attributes.iccid"
                                        id="iccid"
                                    />
                                </FormGroup>
                            </Col>
                            <Col>
                                <FormGroup className="custom-formGroup">
                                    <Label for="phone"><I18nMessage id="sharedPhone" /></Label>
                                    <Input
                                        tag={UInput}
                                        type="text"
                                        name="phone"
                                        id="phone"
                                    />
                                </FormGroup>
                                <FormGroup className="custom-formGroup">
                                    <Label for="m2mProvider"><I18nMessage id="deviceM2mProvider" /></Label>
                                    <Input
                                        tag={UInput}
                                        type="text"
                                        name="attributes.m2mProvider"
                                        id="m2mProvider"
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    </>
                </FieldSet>
                <br />
                <FieldSet className="text-left border p-4" title={<I18nMessage id="sharedVehicle" />}>
                    <>
                        <Row>
                            <Col>
                                <FormGroup className="custom-formGroup">
                                    <Label for="vehicleModel"><I18nMessage id="deviceVehicleModel" /></Label>
                                    <UInput hidden name="attributes.vehicleModel" />
                                    <AsyncTypeahead
                                        id="vehicleModel"
                                        isLoading={fipeLoading}
                                        minLength={2}
                                        delay={350}
                                        defaultInputValue={data?.attributes?.vehicleModel || ''}
                                        options={fipeOptions}
                                        labelKey="label"
                                        filterBy={() => true}
                                        useCache={false}
                                        onSearch={handleFipeSearch}
                                        onChange={handleFipeSelect}
                                        onInputChange={handleFipeInputChange}
                                        placeholder={intl.formatMessage({id: 'deviceFipeSearchPlaceholder'})}
                                        emptyLabel={intl.formatMessage({id: 'deviceFipeSearchEmpty'})}
                                    />
                                </FormGroup>
                                <FormGroup className="custom-formGroup">
                                    <Label for="plate"><I18nMessage id="devicePlate" /></Label>
                                    <Input
                                        tag={UInput}
                                        type="text"
                                        name="attributes.plate"
                                        id="plate"
                                    />
                                </FormGroup>

                                <FormGroup className="custom-formGroup">
                                    <Label for="year"><I18nMessage id="deviceYear" /></Label>
                                    <Input
                                        tag={UInput}
                                        type="text"
                                        name="attributes.year"
                                        id="year"
                                    />
                                </FormGroup>

                                <FormGroup className="custom-formGroup">
                                    <Label for="chassi"><I18nMessage id="deviceChassi" /></Label>
                                    <Input
                                        tag={UInput}
                                        type="text"
                                        name="attributes.chassi"
                                        id="chassi"
                                    />
                                </FormGroup>

                                <FormGroup className="custom-formGroup">
                                    <Label for="installationLocation"><I18nMessage id="deviceInstallationLocation" /></Label>
                                    <Input
                                        tag={UInput}
                                        type="text"
                                        name="attributes.installationLocation"
                                        id="installationLocation"
                                    />
                                </FormGroup>

                            </Col>
                            <Col>

                                <FormGroup className="custom-formGroup">
                                    <Label for="manufacturer"><I18nMessage id="deviceManufacturer" /></Label>
                                    <Input
                                        tag={UInput}
                                        type="text"
                                        name="attributes.manufacturer"
                                        id="manufacturer"
                                    />
                                </FormGroup>

                                <FormGroup className="custom-formGroup">
                                    <Label for="color"><I18nMessage id="deviceColor" /></Label>
                                    <Input
                                        tag={UInput}
                                        type="text"
                                        name="attributes.color"
                                        id="color"
                                    />
                                </FormGroup>

                                <FormGroup className="custom-formGroup">
                                    <Label for="fuelType"><I18nMessage id="deviceFuelType" /></Label>
                                    <Input
                                        tag={UInput}
                                        type="text"
                                        name="attributes.fuelType"
                                        id="fuelType"
                                    />
                                </FormGroup>

                                <FormGroup className="custom-formGroup">
                                    <Label for="renavam"><I18nMessage id="deviceRenavam" /></Label>
                                    <Input
                                        tag={UInput}
                                        type="text"
                                        name="attributes.renavam"
                                        id="renavam"
                                    />
                                </FormGroup>

                                <FormGroup className="custom-formGroup">
                                    <Label for="technician"><I18nMessage id="deviceTechnician" /></Label>
                                    <Input
                                        tag={UInput}
                                        type="text"
                                        name="attributes.technician"
                                        id="technician"
                                    />
                                </FormGroup>

                            </Col>
                        </Row>
                    </>
                </FieldSet>
                <br />
                <FieldSet className="text-left border p-4" title={<I18nMessage id="settingsTitle" />}>
                    <>
                        <Row>
                            <Col>
                                <FormGroup className="custom-formGroup">
                                    <Label for="fuelConsumption"><I18nMessage id="deviceFuelConsumption" /></Label>
                                    <Input
                                        tag={UInput}
                                        type="text"
                                        name="attributes.fuelConsumption"
                                        id="fuelConsumption"
                                    />
                                </FormGroup>
                            </Col>
                            <Col>
                                <FormGroup className="custom-formGroup">
                                    <Label for="speedLimit"><I18nMessage id="deviceSpeedLimit" /> (Km/h)</Label>
                                    <Input
                                        tag={UInput}
                                        type="number"
                                        min={0}
                                        name="attributes.speedLimit"
                                        id="speedLimit"
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <FormGroup className="custom-formGroup">
                                    <Label for="devicePassword"><I18nMessage id="devicePassword" /></Label>
                                    <Input
                                        tag={UInput}
                                        type="text"
                                        name="attributes.devicePassword"
                                        id="devicePassword"
                                    />
                                </FormGroup>
                            </Col>
                            <Col>
                                <FormGroup className="custom-formGroup">
                                    <Label for="ignoreOdometer">
                                        <I18nMessage id={ignoreOdometer ? 'deviceIgnoreOdometerOn' : 'deviceIgnoreOdometerOff'} />
                                    </Label>
                                    <ToggleButton
                                        name={'attributes.ignoreOdometer'}
                                        id={'ignoreOdometer'}
                                        onValueChange={setIgnoreOdometer} />
                                </FormGroup>
                            </Col>
                        </Row>
                    </>
                </FieldSet>
                <UInput hidden name="shareable" value={integrationFlags.shareable} />
                <UInput hidden name="mirrorable" value={integrationFlags.mirrorable} />
                {showIntegrations && (
                    <>
                        <br />
                        <FieldSet className="text-left border p-4" title={<I18nMessage id="sharedIntegrations"/>}>
                            <div className="row">
                                <div className="col">
                                    <IntegrationAccessToggles
                                        owner="device"
                                        integrations={availableIntegrations}
                                        value={selectedIntegrationIds}
                                        onChange={onIntegrationToggle}
                                    />
                                </div>
                            </div>
                        </FieldSet>
                    </>
                )}
                <br />
                <FieldSet className="text-left border p-4" title={intl.formatMessage({id: 'deviceImage'})}>
                    <>
                        <Row style={{paddingLeft: '10px', paddingRight: '10px'}}>
                                <Col xs="12" sm="12" md="12" {...getRootProps({className: `dnd dropzone ${imagesAmount === 0 && '--empty'}`})}>
                                    <input {...getInputProps()} />
                                    {imagesAmount === 0 ? (
                                        <>
                                            <Row className='justify-content-center' style={{marginBottom: '2px'}}>
                                                <i className="fal fa-cloud-upload fa-2x" style={{color: '#16A5DA'}}></i>
                                            </Row>
                                            <Row className='justify-content-center mb-1'>
                                                <span className='--supporting-text --semi-bold'>{intl.formatMessage({id: 'deviceImagesDndTitle'})}</span>
                                            </Row>
                                            <Row className='justify-content-center'>
                                                <span className='--supporting-text'>{intl.formatMessage({id: 'deviceImagesDndSupportedExtensions'})}</span>
                                            </Row>
                                        </>
                                    ) : (
                                        <Row className='pl-2 pr-3'>
                                            {images.map((img, index) => (
                                                <Col key={`${img.id}_preview`} xs="6" md="3" className='pr-2 pl-2'>
                                                    <div className='dnd__preview_container'>
                                                        <Row noGutters>
                                                            <Col xs="12">
                                                                <div className='dnd__preview_container__inner'>
                                                                    <img
                                                                        className='dnd__preview_container__img'
                                                                        src={img.preview}
                                                                        onClick={onPressDeviceImage(img)}
                                                                        alt={img.name}
                                                                    />
                                                                </div>
                                                            </Col>
                                                        </Row>

                                                        {!imageEditionLocked && (
                                                            <Row noGutters>
                                                                <Col xs="12">
                                                                    <div className='dnd__preview_container__actions'>
                                                                        <span className='dnd__preview_container__img_label'>{img.name}</span>
                                                                        <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', flexGrow: 0, gap: 8, fontSize: 11}}>
                                                                            <Tooltip title={intl.formatMessage({id: 'deviceImagePinAction'})}>
                                                                                <IconButton className='az-icon-btn' onClick={pinImage(img)}>
                                                                                    <i data-active={index === 0 ? 'true' : 'false'} className="az-icon-btn__icon fas fa-thumbtack"></i>
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                            <Tooltip title={intl.formatMessage({id: 'deviceImageRemoveAction'})}>
                                                                                <IconButton className='az-icon-btn' onClick={removeImage(img)}>
                                                                                    <i className="az-icon-btn__icon far fa-trash-alt"></i>
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        </div>
                                                                    </div>
                                                                </Col>
                                                            </Row>
                                                        )}
                                                    </div>
                                                </Col>
                                            ))}
                                        </Row>
                                    )}
                                </Col>
                        </Row>

                        <div aria-hidden className='mb-3' />

                        <Row>
                            <div style={{display: 'flex', flexDirection: 'column', flexGrow: 1, maxWidth: '100%', alignItems: 'flex-start'}}>
                                <div style={{display: 'flex', flexDirection: 'row', columnGap: '8px'}}>
                                    <Button
                                        className="mb-0 mt-0 dnd__btn"
                                        onClick={openDeviceImagesPicker}
                                        disabled={imagesAmount === MAX_DEVICE_IMAGES || imageEditionLocked || loadingDeviceImages}
                                    >
                                        {intl.formatMessage({id: 'deviceImagesSelectFile'})}
                                    </Button>

                                    {edit && imageEditionLocked && !loadingDeviceImages && (<Button
                                        className="mb-0 mt-0 dnd__btn_unlock"
                                        onClick={openUnlockDialog}
                                    >
                                        {intl.formatMessage({id: 'deviceImagesUnlockEdition'})}
                                    </Button>)}
                                </div>
                                <span style={{lineHeight: '20px', color: '#424242', fontSize: '8px', fontWeight: 'normal', marginLeft: '10px'}}>{intl.formatMessage({id: 'deviceImagesSelectFileHelper'})}</span>
                            </div>
                        </Row>
                    </>
                </FieldSet>

                <br />
                <GroupButtonForm
                    loading={loading}
                    cancel={cancel}
                    submit={handleF8Key}
                />
            </Form>
            <DeviceImagesOverlay
                ref={imagesOverlayRef}
                images={images}
            />

            <Dialog open={isUnlockDialogOpen} onClose={closeUnlockDialog}
                title={intl.formatMessage({id: 'deviceImagesUnlockDialogTitle'})}
                description={intl.formatMessage({id: 'deviceImagesUnlockDialogContent'})}
                confirmationText={intl.formatMessage({id: 'deviceImagesUnlockDialogConfirm'})}
                onConfirm={acceptUnlockDialog}
                cancelationText={intl.formatMessage({id: 'deviceImagesUnlockDialogCancel'})}
                onCancel={closeUnlockDialog}
            />
        </>
    );
};

export default DeviceAdminForm;
